import debounce from 'just-debounce-it';
import { useContext, useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import FadeIn from '@/components/animations/FadeIn';
import { FormErrorMessage } from '@/components/FormErrorMessage';
import TipTapEditorV3 from '@/components/TipTapEditorV3/TipTapEditorV3';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EditorType } from '@/Enums/enum';
import { StyledHR } from '@/Shared/StyledElements';
import { StyledH5 } from '@/Shared/Typography/typography';
import { useCourseEditStore } from '@/stores/courseEditStore';
import { useCourseLessonsStore } from '@/stores/lessonStore';
import CourseEditContext from '../CourseEditContext';
import { StyledLessonPlans, StyledLessonsPlaceholder } from './CourseLessonPlans.Styles';

interface AddGroupFormValues {
	groupTitle: string;
}

const AddGroupForm = () => {
	const { addLessonSection } = useCourseLessonsStore();
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<AddGroupFormValues>();

	const onSubmit: SubmitHandler<AddGroupFormValues> = ({ groupTitle }) => {
		addLessonSection(groupTitle.trim());
		reset();
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-2">
			<Input
				type="text"
				placeholder="Group title…"
				className="h-8 text-sm"
				{...register('groupTitle', {
					required: 'Title is required',
					validate: (v) => v.trim() !== '' || 'Title is required',
				})}
			/>
			<FormErrorMessage
				className="text-[11px] -mt-1"
				isShowing={!!errors?.groupTitle}
				message={errors.groupTitle?.message}
			/>
			<Button variant="secondary" size="small" type="submit">
				Add Group
			</Button>
		</form>
	);
};

const CourseLessonPlans = () => {
	const { canEdit } = useContext(CourseEditContext);
	const { updateCommentTypes } = useCourseEditStore();
	const { lessons, activeGroupId, activeLessonId, updateGroupTitle, updateLessonField } =
		useCourseLessonsStore();

	useEffect(() => {
		updateCommentTypes(['none']);
	}, [updateCommentTypes]);

	const activeGroup = lessons.find((g) => g.id === activeGroupId);
	const activeLesson = activeGroup?.lessons.find((l) => l.id === activeLessonId);

	if (!activeGroup || !activeLesson) {
		return (
			<StyledLessonsPlaceholder>
				<div className="flex flex-col items-center gap-4 py-12 px-4 text-center max-w-2xl mx-auto">
					<div className="flex flex-col gap-1">
						<p className="font-semibold text-foreground text-[14px]">No lesson plans yet</p>
						<p className="text-[12px] text-muted-foreground">
							Start by creating a group, then add individual lessons inside it.
						</p>
						<p className="hidden md:block text-[12px] text-muted-foreground/70 mt-1">
							Use the panel on the right to create your first group.
						</p>
					</div>
					{/* On mobile the side panel is behind the drawer, so show the form inline */}
					<div className="md:hidden w-full text-left">
						<AddGroupForm />
					</div>
				</div>
			</StyledLessonsPlaceholder>
		);
	}

	const handleGroupTitle = debounce((value: string) => {
		updateGroupTitle(activeGroup.id, value);
	}, 500);

	const handleLessonField = debounce((field: keyof Omit<Lesson, 'id'>, value: string) => {
		updateLessonField(activeGroup.id, activeLesson.id, field, value);
	}, 500);

	return (
		<StyledLessonPlans>
			<FadeIn>
				<div className="editor-wrapper">
					<StyledH5 mb="var(--spacing-4)">Lesson Group Title</StyledH5>
					<Input
						type="text"
						defaultValue={activeGroup.title}
						placeholder="Add a lesson group title..."
						onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
							handleGroupTitle(e.target.value);
						}}
					/>
				</div>
				<StyledHR />
				<div className="editor-wrapper">
					<StyledH5 mb="var(--spacing-4)">Title</StyledH5>
					<Input
						type="text"
						defaultValue={activeLesson.title}
						placeholder="Add a title..."
						onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
							handleLessonField('title', e.target.value);
						}}
					/>
				</div>
				<div className="editor-wrapper">
					<StyledH5 mb="var(--spacing-4)">Objectives</StyledH5>
					<TipTapEditorV3
						isEditable={canEdit}
						content={activeLesson.objectives}
						placeholder="Add objectives ..."
						id={`${activeLesson.id}-objectives`}
						type={EditorType.LESSON}
						onChange={(value) => handleLessonField('objectives', value)}
					/>
				</div>
				<div className="editor-wrapper">
					<StyledH5 mb="var(--spacing-4)">Lesson Content</StyledH5>
					<TipTapEditorV3
						isEditable={canEdit}
						content={activeLesson.body}
						placeholder="Add lessons, activities, resources ..."
						id={`${activeLesson.id}-body`}
						type={EditorType.LESSON}
						onChange={(value) => handleLessonField('body', value)}
					/>
				</div>
			</FadeIn>
		</StyledLessonPlans>
	);
};

export default CourseLessonPlans;
